import { cn } from '@/utils/helpers';

export interface LoaderProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'spinner' | 'dots' | 'pulse';
  className?: string;
  text?: string;
}

const Loader: React.FC<LoaderProps> = ({
  size = 'md',
  variant = 'spinner',
  className,
  text,
}) => {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12',
  };

  if (variant === 'spinner') {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-2', className)}>
        <div
          className={cn(
            'border-2 border-current border-t-transparent rounded-full animate-spin',
            sizes[size]
          )}
          role="status"
          aria-label="Loading"
        />
        {text && <p className="text-sm text-neutral-600">{text}</p>}
      </div>
    );
  }

  if (variant === 'dots') {
    const dotSize = {
      sm: 'w-1.5 h-1.5',
      md: 'w-2 h-2',
      lg: 'w-2.5 h-2.5',
      xl: 'w-3 h-3',
    };

    return (
      <div className={cn('flex flex-col items-center justify-center gap-2', className)}>
        <div className="flex items-center gap-1" role="status" aria-label="Loading">
          <div
            className={cn(
              'bg-current rounded-full animate-bounce',
              dotSize[size]
            )}
            style={{ animationDelay: '0ms' }}
          />
          <div
            className={cn(
              'bg-current rounded-full animate-bounce',
              dotSize[size]
            )}
            style={{ animationDelay: '150ms' }}
          />
          <div
            className={cn(
              'bg-current rounded-full animate-bounce',
              dotSize[size]
            )}
            style={{ animationDelay: '300ms' }}
          />
        </div>
        {text && <p className="text-sm text-neutral-600">{text}</p>}
      </div>
    );
  }

  // Pulse variant
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2', className)}>
      <div
        className={cn(
          'bg-current rounded-full animate-pulse',
          sizes[size]
        )}
        role="status"
        aria-label="Loading"
      />
      {text && <p className="text-sm text-neutral-600">{text}</p>}
    </div>
  );
};

export default Loader;

// Skeleton Loader Component
export interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  count?: number;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className,
  variant = 'text',
  width,
  height,
  count = 1,
}) => {
  const baseStyles = 'animate-pulse bg-neutral-200';

  const variants = {
    text: 'h-4 rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-md',
  };

  const style = {
    width: width ? (typeof width === 'number' ? `${width}px` : width) : '100%',
    height: height
      ? typeof height === 'number'
        ? `${height}px`
        : height
      : undefined,
  };

  if (count > 1) {
    return (
      <div className="space-y-2">
        {Array.from({ length: count }).map((_, index) => (
          <div
            key={index}
            className={cn(baseStyles, variants[variant], className)}
            style={style}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(baseStyles, variants[variant], className)}
      style={style}
      aria-label="Loading..."
    />
  );
};

// Full Page Loader Component
export const FullPageLoader: React.FC<{ text?: string }> = ({ text }) => {
  return (
    <div className="fixed inset-0 bg-white/90 backdrop-blur-sm z-50 flex items-center justify-center">
      <Loader size="xl" variant="spinner" text={text} className="text-primary-600" />
    </div>
  );
};
